import { useState } from 'react'
import { Link } from 'react-router-dom'
import { formatDistanceToNow } from 'date-fns'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import {
  freshnessOf,
  victimDisplayName,
  postExcerpt,
  formatUtcTimestamp,
  type DossierClaim,
  type FreshnessKey,
} from './leak-site-utils'

/**
 * Dense ransomware-claim dossier row (KAN-224). Replaces the old anonymised
 * card on the Leak Sites page with a named-victim dossier:
 *   - victim org name, sector / region / size band
 *   - ransomware group (active dot + threat-actor link when mapped)
 *   - freshness (derived from the real claim timestamp) + relative time
 *   - public post excerpt, related CVEs
 *   - expandable detail with discovered/retrieved timestamps + a GUARDED
 *     source-URL reveal/copy.
 *
 * The source URL is rendered as guarded, copyable plain text — never an
 * `<a href>` — so a stray click can't navigate an analyst to ransom
 * infrastructure. Volume/evidence-screenshot mocks from the design are
 * omitted: that data is not in the contract and we don't fabricate it.
 */

const HEAT_CLASSES: Record<FreshnessKey, string> = {
  live: 'bg-rose-500',
  recent: 'bg-amber-500',
  'this-week': 'bg-amber-400/70',
  aged: 'bg-muted-foreground/40',
}

const FRESH_TEXT: Record<FreshnessKey, string> = {
  live: 'text-rose-600 dark:text-rose-400',
  recent: 'text-amber-600 dark:text-amber-400',
  'this-week': 'text-amber-700 dark:text-amber-300',
  aged: 'text-muted-foreground',
}

function relativeTime(iso: string): string {
  const ms = Date.parse(iso)
  if (Number.isNaN(ms)) return 'unknown'
  return formatDistanceToNow(new Date(ms), { addSuffix: true })
}

/** Best-effort emoji flag from an ISO α2 code; empty string for dirty input. */
function flagFor(iso2: string): string {
  if (!/^[A-Za-z]{2}$/.test(iso2)) return ''
  return iso2
    .toUpperCase()
    .split('')
    .map((c) => String.fromCodePoint(127397 + c.charCodeAt(0)))
    .join('')
}

export interface LeakSiteDossierRowProps {
  claim: DossierClaim
  expanded: boolean
  onToggle: () => void
}

export function LeakSiteDossierRow({
  claim,
  expanded,
  onToggle,
}: LeakSiteDossierRowProps) {
  const fresh = freshnessOf(claim.claimDate)
  const victim = victimDisplayName(claim)
  const excerpt = postExcerpt(claim.description)
  const cves = claim.relatedCves ?? []
  const sizeBand =
    claim.victimSizeBand && claim.victimSizeBand !== 'unknown'
      ? claim.victimSizeBand
      : null
  const flag = claim.victimCountry ? flagFor(claim.victimCountry) : ''
  const detailId = `dossier-detail-${claim.id}`

  const meta: string[] = []
  if (claim.victimSector) meta.push(claim.victimSector)
  if (claim.victimCountry) meta.push(`${flag ? `${flag} ` : ''}${claim.victimCountry}`)
  if (sizeBand) meta.push(sizeBand)

  return (
    <article
      // R28: names the VICTIM ORGANISATION in an attribute, which
      // maskTextSelector does not reach.
      data-ph-no-capture
      aria-label={`Ransomware claim: ${victim}`}
      className={cn(
        'flex overflow-hidden rounded-lg border bg-card transition-colors',
        expanded ? 'border-primary/40' : 'border-border',
      )}
    >
      <div className={cn('w-1 flex-none', HEAT_CLASSES[fresh.key])} aria-hidden="true" />
      <div className="min-w-0 flex-1 p-4">
        {/* top row */}
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="truncate font-heading text-[15px] font-semibold text-foreground">
              {victim}
            </h3>
            {meta.length > 0 ? (
              <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                {meta.map((m, i) => (
                  <span key={m} className="flex items-center gap-1.5">
                    {i > 0 ? <span aria-hidden="true">·</span> : null}
                    {m}
                  </span>
                ))}
              </div>
            ) : (
              <div className="mt-1 text-xs italic text-muted-foreground">
                Victim metadata not extracted
              </div>
            )}
          </div>
          <div className="flex-none text-right">
            <div className="flex items-center justify-end gap-1.5">
              <span
                className={cn(
                  'h-1.5 w-1.5 rounded-full',
                  claim.groupActive ? 'bg-emerald-500' : 'bg-muted-foreground',
                )}
                aria-hidden="true"
              />
              {claim.threatActorId ? (
                <Link
                  to={`/threat-actors/${claim.threatActorId}`}
                  className="font-mono text-[13px] font-medium text-foreground hover:text-primary hover:underline"
                >
                  {claim.groupName}
                </Link>
              ) : (
                <span className="font-mono text-[13px] font-medium text-foreground">
                  {claim.groupName}
                </span>
              )}
            </div>
            <div className="mt-1 flex items-center justify-end gap-1.5 text-[11px]">
              <span className={cn('font-medium', FRESH_TEXT[fresh.key])}>
                {fresh.label}
              </span>
              <span className="font-mono text-muted-foreground">
                {relativeTime(claim.claimDate)}
              </span>
            </div>
          </div>
        </div>

        {/* excerpt */}
        {excerpt ? (
          <p className="mt-2.5 text-[13px] leading-relaxed text-muted-foreground">
            {excerpt}
          </p>
        ) : null}

        {/* footer */}
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
            <span className="text-muted-foreground/70">source</span>
            <span className="text-foreground">{claim.source}</span>
          </span>
          {cves.slice(0, 5).map((cve) => (
            <Link
              key={cve}
              to={`/vulnerabilities/${cve}`}
              className="rounded border border-amber-500/30 bg-amber-500/10 px-1.5 py-0.5 font-mono text-[11px] text-amber-700 hover:bg-amber-500/15 dark:text-amber-300"
            >
              {cve}
            </Link>
          ))}
          <div className="ml-auto">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-7 gap-1 px-2.5 text-[11px]"
              onClick={onToggle}
              aria-expanded={expanded}
              aria-controls={detailId}
            >
              {expanded ? (
                <ChevronDown className="h-3 w-3" aria-hidden="true" />
              ) : (
                <ChevronRight className="h-3 w-3" aria-hidden="true" />
              )}
              {expanded ? 'Collapse' : 'Open dossier'}
            </Button>
          </div>
        </div>

        {/* expanded detail */}
        {expanded ? (
          <div id={detailId} className="mt-4 border-t border-border pt-4">
            {claim.description ? (
              <div>
                <h4 className="mb-1.5 text-[10px] font-medium uppercase tracking-[0.1em] text-muted-foreground">
                  Public post
                </h4>
                <p className="text-[13px] leading-relaxed text-foreground/90">
                  {claim.description}
                </p>
              </div>
            ) : (
              <p className="text-[13px] text-muted-foreground">
                No public post text was captured for this claim.
              </p>
            )}

            <dl className="mt-4 grid grid-cols-2 gap-x-6 gap-y-3 text-[12px]">
              <div>
                <dt className="text-[10px] font-medium uppercase tracking-[0.1em] text-muted-foreground">
                  Claim date
                </dt>
                <dd className="mt-0.5 font-mono text-foreground/90">
                  {formatUtcTimestamp(claim.claimDate) ?? 'unknown'}
                </dd>
              </div>
              <div>
                <dt className="text-[10px] font-medium uppercase tracking-[0.1em] text-muted-foreground">
                  First captured
                </dt>
                <dd className="mt-0.5 font-mono text-foreground/90">
                  {formatUtcTimestamp(claim.retrievedAt) ?? 'unknown'}
                </dd>
              </div>
            </dl>

            <GuardedSourceReveal url={claim.victimUrl} />
          </div>
        ) : null}
      </div>
    </article>
  )
}

/**
 * Guarded reveal/copy for the public source URL. Collapsed by default; after
 * an explicit reveal the URL is shown as copyable `<code>` text — never an
 * `<a href>` — so a stray middle/cmd-click cannot navigate to leak
 * infrastructure. Null URL renders a quiet "no source URL" line.
 */
function GuardedSourceReveal({ url }: { url: string | null }) {
  const [revealed, setRevealed] = useState(false)
  const [copied, setCopied] = useState(false)

  if (!url) {
    return (
      <p className="mt-4 text-[11px] text-muted-foreground" data-testid="dossier-source-none">
        No public source URL on this claim.
      </p>
    )
  }

  async function copy() {
    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard) {
        await navigator.clipboard.writeText(url!)
      }
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      // Analyst can still select-copy the text.
    }
  }

  return (
    <div className="mt-4 rounded-md border border-rose-500/25 bg-rose-500/5 p-3">
      <div className="mb-2 text-[10px] font-medium uppercase tracking-[0.08em] text-rose-600 dark:text-rose-400">
        Guarded source — dangerous destination
      </div>
      {!revealed ? (
        <div className="flex flex-wrap items-center gap-2" data-testid="dossier-source-redacted">
          <span className="font-mono text-[11px] text-muted-foreground">
            [ source URL withheld ]
          </span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-6 px-2 text-[11px]"
            onClick={() => setRevealed(true)}
            data-testid="dossier-source-reveal-button"
          >
            Reveal source URL
          </Button>
        </div>
      ) : (
        <div
          className="flex flex-wrap items-center gap-2"
          data-testid="dossier-source-revealed"
        >
          <code
            className="break-all rounded bg-background px-1.5 py-0.5 font-mono text-[11px] text-foreground"
            data-testid="dossier-source-text"
          >
            {url}
          </code>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-6 px-2 text-[11px]"
            onClick={copy}
            aria-label="Copy source URL"
            data-testid="dossier-source-copy-button"
          >
            {copied ? 'Copied' : 'Copy'}
          </Button>
        </div>
      )}
      <p className="mt-2 text-[11px] text-amber-700 dark:text-amber-300/80">
        Do not open on a corporate endpoint — only inside an isolated browsing
        context.
      </p>
    </div>
  )
}
